import { Link } from 'react-router-dom';
import heroAiPlanningImage from '../assets/screenshots/hero-ai-planning.jpg';
import heroTripOverviewImage from '../assets/screenshots/hero-trip-overview.jpg';
import { features, screenshotSlots, steps } from '../content/tr';
import { siteConfig } from '../config/siteConfig';

function StoreButton({ compact = false }) {
  if (siteConfig.googlePlayUrl) {
    return (
      <a
        className={'store-button' + (compact ? ' store-button-compact' : '')}
        href={siteConfig.googlePlayUrl}
        target="_blank"
        rel="noreferrer"
      >
        <span aria-hidden="true">▶</span>
        <span>
          <small>Google Play’den</small>
          İndirin
        </span>
      </a>
    );
  }

  return (
    <span
      className={'store-button is-coming' + (compact ? ' store-button-compact' : '')}
      aria-label="Tripzy yakında Google Play'de"
    >
      <span aria-hidden="true">▶</span>
      <span>
        <small>Yakında</small>
        Google Play’de
      </span>
    </span>
  );
}

function HeroPhones() {
  return (
    <div className="hero-visual">
      <div className="phone phone-back">
        <div className="phone-camera" aria-hidden="true" />
        <img
          className="phone-screen-image hero-ai-screen"
          src={heroAiPlanningImage}
          width="1080"
          height="2031"
          loading="eager"
          decoding="async"
          alt="Tripzy uygulamasında AI tur planlama seçeneklerinin açıldığı ekran"
        />
      </div>
      <div className="phone phone-front">
        <div className="phone-camera" aria-hidden="true" />
        <img
          className="phone-screen-image hero-trip-screen"
          src={heroTripOverviewImage}
          width="972"
          height="2523"
          loading="eager"
          decoding="async"
          fetchPriority="high"
          alt="Tripzy uygulamasında üç günlük Paris gezi planının harita ve plan özeti"
        />
      </div>
      <span className="floating-chip chip-ai">✦ AI planın hazır</span>
      <span className="floating-chip chip-route">↝ En uygun rota</span>
    </div>
  );
}

export default function HomePage() {
  return (
    <>
      <section className="hero-section">
        <div className="hero-orb hero-orb-one" aria-hidden="true" />
        <div className="hero-orb hero-orb-two" aria-hidden="true" />
        <div className="shell hero-grid">
          <div className="hero-copy">
            <span className="eyebrow">Yolculuğun sana özel olsun</span>
            <h1>
              Gezini planla.
              <span>Şehri kendi ritminde keşfet.</span>
            </h1>
            <p>
              Tripzy; ilgi alanlarını, zamanını ve ulaşım tercihini anlayarak
              sana uygulanabilir bir gezi planı çıkarır. Keşiften navigasyona
              kadar her adım tek yerde.
            </p>
            <div className="hero-actions">
              <StoreButton />
              <a className="text-link" href="#how-it-works">
                Nasıl çalıştığını gör <span aria-hidden="true">↓</span>
              </a>
            </div>
            <div className="hero-proof" aria-label="Tripzy öne çıkan faydaları">
              <span>✓ Kişiselleştirilmiş plan</span>
              <span>✓ Esnek rota</span>
              <span>✓ Hesap kontrolü</span>
            </div>
          </div>
          <HeroPhones />
        </div>
      </section>

      <section className="section" id="features">
        <div className="shell">
          <div className="section-heading">
            <span className="eyebrow">Bir gezi uygulamasından fazlası</span>
            <h2>Planın da rotan da seninle uyumlu</h2>
            <p>
              Karmaşık araştırma ve dağınık notlar yerine, kararlarını kolaylaştıran
              tek bir gezi alanı.
            </p>
          </div>
          <div className="feature-grid">
            {features.map((feature, index) => (
              <article className="feature-card" key={feature.title}>
                <div className={'feature-icon icon-' + (index + 1)}>
                  {feature.icon}
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section workflow-section" id="how-it-works">
        <div className="shell">
          <div className="section-heading centered">
            <span className="eyebrow">Üç adımda yola çık</span>
            <h2>Fikrinden günlük rotana kadar</h2>
          </div>
          <div className="steps-grid">
            {steps.map((step, index) => (
              <article className="step-card" key={step.number}>
                <span className="step-number">{step.number}</span>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
                {index < steps.length - 1 && (
                  <span className="step-connector" aria-hidden="true">→</span>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section showcase-section">
        <div className="shell">
          <div className="section-heading split-heading">
            <div>
              <span className="eyebrow">Her adım gözünün önünde</span>
              <h2>Keşfet, düzenle, ilerle</h2>
            </div>
            <p>
              Tripzy’nin gerçek uygulama ekranlarında planlama, günlük rota
              ve navigasyon akışını yakından incele.
            </p>
          </div>
          <div className="screenshot-grid">
            {screenshotSlots.map((slot) => (
              <article className={'screenshot-slot slot-' + slot.accent} key={slot.title}>
                <div className="screenshot-device">
                  <span className="screenshot-speaker" aria-hidden="true" />
                  <div className="screenshot-media">
                    <img
                      src={slot.image}
                      width={slot.width}
                      height={slot.height}
                      loading="lazy"
                      decoding="async"
                      alt={slot.alt}
                    />
                  </div>
                </div>
                <div className="screenshot-caption">
                  <strong>{slot.title}</strong>
                  <span>{slot.description}</span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section trust-section">
        <div className="shell trust-card">
          <div className="trust-symbol" aria-hidden="true">✓</div>
          <div>
            <span className="eyebrow">Kontrol sende</span>
            <h2>Verilerin ve hesabın için açık seçenekler</h2>
            <p>
              Hangi verilerin işlendiğini inceleyebilir, destek ekibine
              ulaşabilir ve doğrulanmış hesap silme başvurusu oluşturabilirsin.
            </p>
          </div>
          <div className="trust-actions">
            <Link className="button button-primary" to="/privacy">
              Gizliliği incele
            </Link>
            <Link className="button button-secondary" to="/delete-account">
              Hesap işlemleri
            </Link>
          </div>
        </div>
      </section>

      <section className="section final-cta-section">
        <div className="shell final-cta">
          <div>
            <span className="eyebrow eyebrow-light">Bir sonraki rotan burada başlasın</span>
            <h2>Şehri listelerle değil, yaşayarak keşfet.</h2>
          </div>
          <StoreButton compact />
        </div>
      </section>
    </>
  );
}
